<script lang="ts" setup>
import { ToccoModuleFieldsFragment, ToccoSeminarFieldsFragment } from '~/api/graphql/generated'
import type { ParsedToccoModuleFieldsFragment, ToccoFieldData } from '~/types'

type SeminarModuleField =
  ToccoSeminarFieldsFragment['fieldRequiredModules'] | ToccoSeminarFieldsFragment['fieldOptionalModules']
type ModuleType = 'required' | 'optional' | 'intro'

const { t } = useI18n()
const store = useSeminarCartStore()

const props = defineProps<{
  entity: ToccoSeminarFieldsFragment
}>()

onBeforeMount(() => {
  store.setSeminar(props.entity)
  store.clearModules()
})

const showErrors = ref(false)

const selectedSeminarModulesCount = ref(() => store.modules.length)

const decodeToccoData = (entity: ToccoModuleFieldsFragment): ParsedToccoModuleFieldsFragment => {
  const clonedEntity = JSON.parse(JSON.stringify(entity)) as ToccoModuleFieldsFragment
  return {
    ...clonedEntity,
    fieldToccoData: JSON.parse(clonedEntity.fieldToccoData ?? '{}') as ToccoFieldData,
  }
}

const filterModuleType = (module: ToccoModuleFieldsFragment, type: ModuleType): boolean => {
  if (type === 'required') {
    return true
  }
  const typename = module.__typename as string
  if (type === 'intro') {
    return typename === 'ToccoIntroModule'
  }
  return typename !== 'ToccoIntroModule'
}

const groupModules = (
  modules: SeminarModuleField,
  type: ModuleType = 'required',
): Record<string, ParsedToccoModuleFieldsFragment[]> => {
  const groupedModules: Record<string, ParsedToccoModuleFieldsFragment[]> = {}
  ;(modules ?? [])
    .map((moduleData) => moduleData?.entity as ToccoModuleFieldsFragment | null | undefined)
    .filter((module): module is ToccoModuleFieldsFragment =>
      Boolean(module?.fieldToccoAbbreviation && filterModuleType(module, type) && module.status),
    )
    .sort((a, b) => (a.fieldToccoAbbreviation ?? '').localeCompare(b.fieldToccoAbbreviation ?? ''))
    .map(decodeToccoData)
    .forEach((module) => {
      if (!module.fieldToccoAbbreviationBase) return
      const group = groupedModules[module.fieldToccoAbbreviationBase] ?? []
      group.push(module)
      groupedModules[module.fieldToccoAbbreviationBase] = group
    })
  Object.keys(groupedModules).forEach((abreviationBase) => {
    groupedModules[abreviationBase] = (groupedModules[abreviationBase] ?? []).sort((a, b) =>
      (a.fieldStartDate?.value ?? '').localeCompare(b.fieldStartDate?.value ?? ''),
    )
  })
  return groupedModules
}

const categories = [
  {
    title: props.entity.fieldRequiredModulesTitle ?? t('tocco.detail.seminarModules.requiredModules'),
    subtitle: props.entity.fieldRequiredModulesSubtitle?.processed || '',
    modules: groupModules(props.entity.fieldRequiredModules),
  },
  {
    title: props.entity.fieldOptionalModulesTitle ?? t('tocco.detail.seminarModules.optionalModules'),
    subtitle: props.entity.fieldOptionalModulesSubtitle?.processed || '',
    modules: groupModules(props.entity.fieldOptionalModules, 'optional'),
  },
  {
    title: props.entity.fieldIntroModulesTitle ?? t('tocco.detail.seminarModules.introModules'),
    subtitle:
      props.entity.fieldIntroModulesSubtitle?.processed ?? t('tocco.detail.seminarModules.introModulesDescription'),
    modules: groupModules(props.entity.fieldOptionalModules, 'intro'),
  },
]
</script>

<template>
  <SectionToccoSeminarModulesCategory
    v-for="category in categories"
    :key="category.title"
    :title="category.title"
    :subtitle="category.subtitle"
    :modules="category.modules"
  />

  <Section large class="button-wrapper">
    <div class="tl-7 tl-o-1">
      <span v-if="showErrors && !selectedSeminarModulesCount()">
        {{ t('tocco.detail.seminarModules.error') }}
      </span>
    </div>

    <div class="tl-4">
      <Button
        primary
        slide
        v-bind="selectedSeminarModulesCount() ? { to: '/registration/seminar' } : { disabled: true }"
        :title="t('tocco.detail.seminarModules.registerButtonText')"
        @click="showErrors = true"
      >
        {{ t('tocco.detail.seminarModules.registerButtonText') }}
      </Button>
    </div>
  </Section>
</template>

<style lang="scss" scoped>
.button-wrapper {
  align-items: center;
  row-gap: var(--gap);

  :deep(.button) {
    width: 100%;
  }
}
</style>
